import type { PosSaleGatewayLine, PosSalePromotionClaimEvidence, PosSaleSyncResult } from "./pos-sale-client";

export type PosCreditSaleSyncInput = {
  deviceSecret: string;
  sessionToken: string;
  localTransactionId: string;
  lines: PosSaleGatewayLine[];
  catalogRevision: number;
  localTotal: number;
  localCommittedAt: string;
  executionMode: "ONLINE" | "OFFLINE";
  requestKey: string;
  clientContext?: Record<string, unknown>;
  promotionClaimEvidence?: PosSalePromotionClaimEvidence[];
};

export type PosCreditSaleGatewayResult = {
  ok: boolean;
  data?: PosSaleSyncResult;
  error?: string;
};

export class PosCreditSaleGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: { baseUrl: string; timeoutMs?: number }) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 15_000;
  }

  async sync(input: PosCreditSaleSyncInput): Promise<PosCreditSaleGatewayResult> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await fetch(`${this.baseUrl}/api/pos/runtime/sale`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          device_secret: input.deviceSecret,
          session_token: input.sessionToken,
          local_transaction_id: input.localTransactionId,
          lines: input.lines,
          catalog_revision: input.catalogRevision,
          local_total: input.localTotal,
          tendered_amount: null,
          local_change_amount: null,
          local_committed_at: input.localCommittedAt,
          execution_mode: input.executionMode,
          settlement_kind: "RECEIVABLE",
          payment_method_code: "",
          receiving_destination_id: null,
          verification_source: "",
          verification_note: null,
          request_key: input.requestKey,
          client_context: input.clientContext ?? {},
          promotion_claim_evidence: input.promotionClaimEvidence ?? [],
        }),
        credentials: "omit",
        signal: controller.signal,
      });
      const payload = (await response.json().catch(() => null)) as PosCreditSaleGatewayResult | null;
      if (!response.ok || !payload?.ok || !payload.data) {
        return { ok: false, error: payload?.error ?? `GATEWAY_HTTP_${response.status}` };
      }
      return payload;
    } catch (error) {
      return {
        ok: false,
        error: error instanceof DOMException && error.name === "AbortError" ? "GATEWAY_TIMEOUT" : "GATEWAY_UNREACHABLE",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
